/**
 * ListDataSource — computed `list` row that aggregates the cells of
 * its direct source-row children.
 *
 * The `calendar-list` alias template hydrates one `list` row plus
 * one child dataSource per visible calendar. Renderers hang off the
 * `list` row so they read one merged event stream from their parent
 * instead of peeking sideways across sibling calendar rows.
 *
 * Range-loading: its loader (`declareRowLoader`) issues a single
 * `fetchCellsBatch` covering all constituent SourceRow children
 * instead of N individual `fetchCells` calls. The aggregate's
 * dynamic children (e.g. one per Google calendar in calendar-list)
 * are computed client-side, so the server can't enumerate them
 * from sheet membership — the client must hand the rowkey list to
 * the batch endpoint.
 *
 * The batch is wrapped in a `RangeBasedFetcher` (mirroring
 * `SourceRow.fetcher()`) so the calendar-render path — which asks
 * for cells one day at a time across the visible window — coalesces
 * into a single padded batch POST instead of one POST per day.
 * Without the fetcher, a 64-day calendar render fired 64 batch
 * requests; with it, the first call seeds a 56-day window (28 days
 * padding on each side, the fetcher's default) and subsequent
 * per-day requests short-circuit on `effectiveRange` coverage.
 */

import type { Option } from "../../core/option.ts";
import { sheetInternals, sheetOf } from "../../sheet/sheet-stores.ts";
import { type DateUnit, DateTime, NaiveDate, Utc } from "@tento-chrono";
import { RangeBasedFetcher } from "@tento-core/range-based-fetcher";
import { Tracing } from "@tento-core/tracing";

import { type BatchRowCells, BatchFetchThrottledError } from "../../backends/rows-backend.ts";
import type { CellEntry } from "../../core/dtype.ts";
import { FormulaEngine } from "../../formula/formula-engine.ts";
import { ComputedDataSource, declareRecomputeMemoByInputs } from "./computed-data-source.ts";
import { declareRowLoader } from "../row-load.ts";
import { SourceCells } from "./source-cells.ts";
import { SourceRow } from "./source-row.ts";
import { SheetNetworkKey } from "../../ids/sheet-network-key.ts";
import { RowNetworkKey } from "../../ids/row-network-key.ts";
import { type SyncKey } from "../../ids/ids.ts";
import { Internals } from "../../core/internals.ts";

// Monotonic instance stamp for `ListDataSource.generation` (below).
let nextListSourceGeneration = 1;

/** `ListDataSource`'s friend record (`Internals`): its constructor registers each instance. */
type ListDataSourceInternals = {
  readonly markBatchFetched: (row: ListDataSource, range: NaiveDate.Range) => void;
  readonly reportBatchFetch: (
    row: ListDataSource,
    range: NaiveDate.Range,
    result: ReadonlyMap<RowNetworkKey, BatchRowCells>,
  ) => void;
};
const listSourceInternals = new Internals<ListDataSource, ListDataSourceInternals>("ListDataSource");

/**
 * Tell `row`'s batch fetcher that `range` has already been loaded, so a later
 * `ensureLoaded` inside it short-circuits. `Sheet.ensureLoaded` calls it after its
 * own batch covers the aggregate's constituents; a friend of the package,
 * not a member of the row and not on a barrel.
 */
export function markBatchFetchedRange(row: ListDataSource, range: NaiveDate.Range): void {
  listSourceInternals.of(row).markBatchFetched(row, range);
}

/**
 * Report `row`'s share of a batch the sheet's prefetch fetched (its rows,
 * cells and sync keys) without registering sync polling: `SheetPrefetch`
 * hands an ephemeral sheet's batch here, which polls nothing. A friend of the
 * package, not a member of the row and not on a barrel.
 */
export function reportBatchFetch(
  row: ListDataSource,
  range: NaiveDate.Range,
  result: ReadonlyMap<RowNetworkKey, BatchRowCells>,
): void {
  listSourceInternals.of(row).reportBatchFetch(row, range, result);
}

/**
 * The computed `list` row that merges its child source rows' cells into one stream, which a
 * calendar-list alias's renderers read.
 */
export class ListDataSource extends ComputedDataSource {
  static readonly #internals: ListDataSourceInternals = {
    markBatchFetched: (row, range) => row.#markBatchFetchedRange(range),
    reportBatchFetch: (row, range, result) => {
      row.#reportBatchFetch(range, result);
    },
  };

  /** A list aggregate (`ComputedDataSource`'s arguments); registers its friend record (`markBatchFetchedRange`). */
  constructor(...args: ConstructorParameters<typeof ComputedDataSource>) {
    super(...args);
    listSourceInternals.register(this, ListDataSource.#internals);
  }

  static {
    // An aggregate's value is one batch over its constituents; that batch
    // loads them, so its tree walks no further.
    declareRowLoader(ListDataSource, {
      value: (row, cx) => row.#loadBatch(cx.range, cx.padding),
      children: () => [],
    });
    // The rewrite clears the row's cells first, so its memo compares
    // cell-store revision tokens (no cell read) instead of a signature.
    declareRecomputeMemoByInputs(
      ListDataSource,
      (row, range) => row.#inputsKey(range),
      (row) => row.#dependencySignature(),
    );
  }

  /**
   * Instance stamp. Alias re-expansion that drops and rebuilds this aggregate
   * mints a NEW instance, with an empty `rangeBatchFetcher` and so no range
   * coverage. `Sheet._performPrefetch` logs the stamps of the aggregates it
   * sees (`sheet/prefetch/plan` `aggGens`), and a climbing stamp on an
   * identical range there is the recreation-storm signature that
   * calendar-switch-refetch-budget reads. 9757a7b2 dropped the field together
   * with the aggregate-side breadcrumb, but that `aggGens` reader stayed and
   * logged `null` for every aggregate.
   */
  readonly generation: number = nextListSourceGeneration++;

  override get defaultCollapsedInPropertyList(): boolean {
    return true;
  }

  /**
   * Lazy-init `RangeBasedFetcher` matching the SourceRow pattern at
   * `source-row.ts:103-113`. Owns 28-day padding (default), in-flight
   * coalescing, and effective-range tracking so repeated overlapping
   * `ensureLoaded` calls (per-day calendar render) collapse to a single
   * batch POST. Lazy because aggregates with no constituents never
   * fetch.
   */
  #rangeBatchFetcher: Option<RangeBasedFetcher<Utc>> = null;

  #batchFetcher(): RangeBasedFetcher<Utc> {
    if (this.#rangeBatchFetcher) return this.#rangeBatchFetcher;
    this.#rangeBatchFetcher = new RangeBasedFetcher<Utc>(async (utcRange) => {
      const naiveRange = new NaiveDate.Range(
        utcRange.start.date,
        utcRange.end.date,
      );
      await this.#performBatchFetch(naiveRange);
    });
    return this.#rangeBatchFetcher;
  }

  /**
   * Direct child source rows that feed this aggregate list.
   * Renderer children and layout/alias infrastructure are ignored.
   */
  constituentRows(): readonly SourceRow[] {
    const rows: SourceRow[] = [];
    for (const child of this.children) {
      if (child instanceof SourceRow) rows.push(child);
    }
    return rows;
  }

  /**
   * Single-roundtrip range load (this row's `declareRowLoader` value) —
   * coalesced via `RangeBasedFetcher`. The first call seeds a padded 56-day
   * window (28 days on each side, the fetcher's default — same convention
   * every other `RangeBasedFetcher` call site in the codebase relies on);
   * subsequent overlapping calls short-circuit on `effectiveRange`. Disjoint
   * ranges issue a separate batch; sibling aggregates asking for the same
   * range in one tick share one POST (`SheetInternals.gatherAggregateBatch`).
   */
  async #loadBatch(
    range: NaiveDate.Range,
    padding: Option<DateUnit>,
  ): Promise<void> {
    if (this.constituentRows().length === 0) return;
    // A sheet-wide prefetch in flight over this range already carries every
    // constituent, and marks this aggregate covered when it lands: wait for it
    // instead of posting the same rows again. Then this fetcher answers from
    // that coverage, or fetches what the prefetch did not deliver. A nested
    // aggregate (a constituent of another) is one of the rows that prefetch
    // loads, and its per-row fallback awaits this very call: it never waits.
    const covering = this.parent instanceof ListDataSource
      ? null
      : sheetInternals(sheetOf(this)).coveringPrefetch(range);
    // `true`: the covering prefetch delivered — count `range` loaded here too,
    // since this fetcher's one-interval claim may have
    // forgotten a piece that landed out of order (judge P r31 P3: re-posted
    // ranges under latency).
    if (covering != null && (await covering)) this.#markBatchFetchedRange(range);
    const utcRange = range.withTime().withTz(Utc);
    if (padding == null) await this.#batchFetcher().fetchRange(utcRange);
    else await this.#batchFetcher().fetchRange(utcRange, padding);
  }

  /**
   * Tell the batch fetcher that `range` has already been loaded, so
   * subsequent `ensureLoaded(...)` calls inside `range` short-circuit.
   * Called by `Sheet.ensureLoaded` after its own batch covers this
   * aggregate's constituents. Unions with prior coverage — Sheet's
   * prefetch issues tail-only slices, so clobbering would drop the
   * earlier window.
   */
  #markBatchFetchedRange(range: NaiveDate.Range): void {
    if (this.constituentRows().length === 0) return;
    const f = this.#batchFetcher();
    const next = range.withTime().withTz(Utc);
    f.noteInstalled(next);
  }

  /**
   * Issue one batch POST covering every constituent rowKey for
   * `range`, distribute `ok` cells into per-child stores, and fall
   * back to the per-row `ensureLoaded` path for any constituent whose
   * batch entry came back `forbidden` / `not_found` / `error` so
   * the aggregate still surfaces partial data when one calendar's
   * Google auth has expired.
   */
  async #performBatchFetch(range: NaiveDate.Range): Promise<void> {
    const constituents = this.constituentRows();
    if (constituents.length === 0) return;

    const keysToRows = new Map<RowNetworkKey, SourceRow>();
    for (const child of constituents) {
      keysToRows.set(child.backendRowKey(), child);
    }

    // P1: an ephemeral sheet gates the batch against the owned `:calendar`
    // key — the ephemeral sheet's own UUID is invalid server-side, but the
    // canonical constituents resolve by rowKey. Per-row fallback for anything
    // the batch can't serve, and for the whole set if `:calendar` isn't
    // resolvable. (Same fix as sheet.ts `_performPrefetch`; see there for the
    // full rationale.) A persisted sheet batches against its own key, and its
    // batch failure is the load's.
    const ephemeral = sheetOf(this).ephemeral;
    const fallbacks: SourceRow[] = [];
    try {
      // One POST shared with sibling aggregates asking for this range in
      // the same tick (`SheetInternals.gatherAggregateBatch` installs the `ok` entries).
      const batched = await sheetInternals(sheetOf(this)).gatherAggregateBatch(
        SheetNetworkKey.fromWire(ephemeral ? ":calendar" : sheetOf(this).networkKey),
        keysToRows,
        range,
      );
      for (const [rowKey, child] of keysToRows) {
        const entry = batched.get(rowKey);
        if (!(entry && entry.status === "ok")) fallbacks.push(child);
      }
      this.watchBatchSyncKeys(range, batched);
    } catch (err) {
      if (!ephemeral) throw err;
      // Loud but NON-FATAL (mirrors the sheet prefetch): the ephemeral batch
      // failed, so we degrade to a per-row fanout. Keep it visible in console.
      // Still throttled after the paced retries is load, not a fault: warn,
      // and fail the load instead of fanning out.
      const detail = {
        where: "aggregate.performBatchFetch",
        error: err instanceof Error ? err.message : String(err),
        keys: keysToRows.size,
      };
      if (err instanceof BatchFetchThrottledError) {
        // Load, not a fault, and never a fan-out into the 429 window:
        // the load fails with the throttle and records no coverage, so a
        // later bind reads the range again.
        Tracing.warn("net")(
          "ephemeral batch fetch still throttled (429); no per-row fallback",
          detail,
        );
        throw err;
      } else {
        sheetInternals(sheetOf(this)).reportRequestFailure(
          err instanceof Error ? err : new Error(String(err)),
          () => Tracing.err("net")(
            "ephemeral batch fetch failed; falling back to per-row", detail,
          ),
        );
      }
      fallbacks.push(...keysToRows.values());
    }
    if (fallbacks.length > 0) {
      await Promise.all(fallbacks.map((child) => child.ensureLoaded(range, null)));
    }
    FormulaEngine.recomputeFormulas(sheetOf(this), range);
  }

  /**
   * Sync-completion tracking for the aggregate batch path.
   *
   * A batch response reports each constituent's in-flight background
   * syncs via `syncKeys` (server-side thread #3: the batch handler
   * surfaces the orchestrator poll keys the bulk path already carries).
   * When Google's viewport/backfill pull for a calendar is still in
   * flight, the batch returns stale/empty cells now and populates the
   * DB a beat later — with no client signal, the calendar showed
   * "no events until refresh". The keys are handed to the SHEET, which
   * polls each one once client-wide and, on completion, issues one
   * coalesced refetch (`Sheet.watchSyncKeys`) — the refetch writes
   * `canonicalCells` → emits `ColumnDataChanged` → the binder rebinds the
   * affected columns (the exact path a manual refresh exercises).
   *
   * This aggregate used to poll its keys itself and refetch the full batch
   * once PER completed key: every aggregate (and every re-hydrated sheet)
   * ran its own loop for the same key, so a cold account with ~40 keys in
   * flight issued 100-180 refetches and hundreds of status GETs, and hit
   * the per-user rate limit (the 2026-09-24 storm).
   */
  // Public (package-internal, `_`-prefixed) so
  // `Sheet._performPrefetch` — the sheet-wide coordinator that issues the
  // INITIAL batch fetch and then `markBatchFetchedRange` (which short-circuits
  // this aggregate's own `ensureLoaded`) — can hand its batch result here. Without
  // this the first load's syncKeys were dropped: the calendar never polled and
  // events only appeared after a manual refresh.
  watchBatchSyncKeys(
    range: NaiveDate.Range,
    result: ReadonlyMap<RowNetworkKey, BatchRowCells>,
  ): void {
    const keys = this.#reportBatchFetch(range, result);
    if (keys.size > 0) sheetInternals(sheetOf(this)).watchSyncKeys(keys, range);
  }

  /** Report the actual aggregate batch without registering sync polling (`reportBatchFetch`). */
  #reportBatchFetch(
    range: NaiveDate.Range,
    result: ReadonlyMap<RowNetworkKey, BatchRowCells>,
  ): Map<SyncKey, Set<RowNetworkKey>> {
    // Each key with the row keys whose entries reported it: a completion
    // refetches those rows only.
    const keys = new Map<SyncKey, Set<RowNetworkKey>>();
    let okRows = 0;
    let cells = 0;
    // Scope to THIS aggregate's own constituents — the prefetch result covers
    // every aggregate's rows, so filter by our constituent rowKeys.
    for (const child of this.constituentRows()) {
      const rowKey = child.backendRowKey();
      const entry = result.get(rowKey);
      if (entry && entry.status === "ok") {
        okRows++;
        cells += entry.cells.length;
        if (entry.cells.length === 0 && (entry.syncKeys?.length ?? 0) === 0) this.#noteEmptyEventsRow(rowKey, range);
        for (const key of entry.syncKeys ?? []) {
          const rows = keys.get(key) ?? new Set<RowNetworkKey>();
          rows.add(rowKey);
          keys.set(key, rows);
        }
      }
    }
    // [temp instrumentation] Confirms whether the batch response carried
    // syncKeys for this account/session, how many cells it installed, and
    // (via the key strings) which stages/ranges are syncing.
    Tracing.log("batch-sync/fetch", {
      hasPoll: !!sheetOf(this).syncPoll,
      constituents: this.constituentRows().length,
      okRows,
      cells,
      syncKeyCount: keys.size,
      syncKeys: [...keys.keys()],
      rangeStart: String(range.start),
      rangeEnd: String(range.end),
    });
    return keys;
  }

  /** The advertised events rows this aggregate has reported empty (`calendar/events-row-empty-shim`), by wire key. */
  readonly #emptyEventsRowsTraced = new Set<string>();

  /**
   * An advertised `:~…:events` row the server answered `ok` with no cell and
   * no sync in flight. An unknown key is answered as an empty shim row, so a
   * calendar whose advertised key nobody writes (a seeder that keyed its
   * rows under another source) painted nothing and was indistinguishable
   * from an empty calendar in every trace the client emits. Trace it once per aggregate and key, with the range the batch
   * fetched, so a wrong key is visible in the console log a runner
   * captures; a calendar
   * that is empty over the range traces the same line once and is otherwise
   * unaffected. A row with a sync in flight is not empty yet: its cells land
   * on the sync's completion refetch.
   */
  #noteEmptyEventsRow(rowKey: RowNetworkKey, range: NaiveDate.Range): void {
    if (!rowKey.endsWith(":events") || this.#emptyEventsRowsTraced.has(rowKey)) return;
    this.#emptyEventsRowsTraced.add(rowKey);
    Tracing.log("calendar/events-row-empty-shim", { key: rowKey, rangeStart: String(range.start), rangeEnd: String(range.end) });
  }

  /** Read merged cells from constituents without mutating the row store.
   *  Unions BOTH lanes — the semantic date lane and the timepoint lane (timed
   *  events live there after the substrate deconfliation). A timepoint cell is
   *  projected to a day-keyed `CellEntry` (the aggregate is a derived display
   *  view; renderers position by the event value, not the cell key), so a
   *  date-lane-only merge no longer silently drops timed events. */
  aggregateCells(range: Option<NaiveDate.Range>): readonly CellEntry[] {
    const rows = this.constituentRows();
    const merged = new Map<string, CellEntry>();
    for (const entry of this.#mergeCells(rows, range)) {
      merged.set(cellKey(entry), entry);
    }
    for (const child of rows) {
      for (const tp of SourceCells.customs(SourceCells.read(child, range))) {
        const entry: CellEntry = {
          date: NaiveDate.Partial.fromDate(tp.dtr.start.date),
          id: tp.id,
          value: tp.value,
          style: tp.style,
        };
        merged.set(cellKey(entry), entry);
      }
    }
    return [...merged.values()];
  }

  /** Compute the merged cells for the current constituent row set. */
  override computeCellsForRange(range: NaiveDate.Range): CellEntry[] {
    return this.#mergeCells(this.constituentRows(), range);
  }

  #mergeCells(
    rows: readonly SourceRow[],
    range: Option<NaiveDate.Range>,
  ): CellEntry[] {
    const merged = new Map<string, CellEntry>();

    for (const child of rows) {
      for (const entry of SourceCells.dates(SourceCells.read(child, range))) {
        merged.set(cellKey(entry), entry);
      }
    }

    return [...merged.values()];
  }

  /**
   * The rewrite's inputs as identity tokens: the range, the attribute bag
   * (frozen anew per change), each constituent's id and `cellsRevision()`,
   * then this row's `cellsRevision()` (taken after the rewrite).
   */
  #inputsKey(range: NaiveDate.Range): readonly unknown[] {
    const key: unknown[] = [range.start.dse, range.end.dse, this.attributes()];
    for (const row of this.constituentRows()) key.push(row.id, ...row.cellsRevision());
    key.push(...this.cellsRevision());
    return key;
  }

  /**
   * The constituents as the ledger shadow's referee reads a dependency
   * signature: `{ row, rev }` per constituent, its cell-store revision
   * (`cellsRevision()`), so the referee costs no cell read; built only while a
   * shadow is attached.
   */
  #dependencySignature(): string {
    return JSON.stringify(this.constituentRows().map((row) => ({ row: row.id.toString(), rev: row.cellsRevision()[1] })));
  }
}

function cellKey(entry: CellEntry): string {
  return `${entry.date.toString()}|${entry.id ?? ""}`;
}
